import React, { useState, useRef, useEffect } from 'react';
import { buildApiUrl } from '../config/api';
import { useDashboardStore } from '../store/dashboardStore';

export const SimpleAIInsights: React.FC = () => {
    const [insights, setInsights] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [question, setQuestion] = useState<string>('');
    const [chatHistory, setChatHistory] = useState<any[]>([]);
    const [conversationId, setConversationId] = useState<string | null>(null);
    const chatEndRef = useRef<HTMLDivElement>(null);
    const { filters } = useDashboardStore();

    // Auto-scroll to bottom of chat
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatHistory]);

    const testBedrock = async () => {
        try {
            setLoading(true);
            setError(null);
            console.log('🤖 Testing Bedrock connection...');

            const response = await fetch(buildApiUrl('/api/insights/test'));
            const result = await response.json();

            if (result.success) {
                console.log('✅ Bedrock connection successful!');
                setError(null);
            } else {
                console.error('❌ Bedrock connection failed:', result.error);
                setError(`Bedrock connection failed: ${result.error}`);
            }
        } catch (err) {
            console.error('❌ Bedrock test error:', err);
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setLoading(false);
        }
    };

    const generateInsights = async (type: string = 'utilization') => {
        try {
            setLoading(true);
            setError(null);
            console.log(`🧠 Generating ${type} insights...`);

            const response = await fetch(buildApiUrl('/api/insights/quick'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    type,
                    filters: {
                        quarter: filters?.quarter || undefined,
                        datasetTag: (filters as any)?.datasetTag || undefined
                    }
                })
            });

            const result = await response.json();
            console.log('🎯 Insights result:', result);

            if (result.success) {
                setInsights(result.insights);
                setError(null);
            } else {
                setError(`Failed to generate insights: ${result.error}`);
            }
        } catch (err) {
            console.error('❌ Insights generation error:', err);
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setLoading(false);
        }
    };

    const askQuestion = async () => {
        if (!question.trim()) return;

        try {
            setLoading(true);
            setError(null);

            // Add user question to chat history immediately
            const userMessage = {
                id: Date.now().toString(),
                type: 'user',
                content: question,
                timestamp: new Date()
            };
            setChatHistory(prev => [...prev, userMessage]);

            const currentQuestion = question;
            setQuestion(''); // Clear input immediately

            console.log(`❓ Asking question: ${currentQuestion}`);

            const response = await fetch(buildApiUrl('/api/insights/chat'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    question: currentQuestion,
                    conversationId: conversationId,
                    sessionId: conversationId,
                    filters: {
                        quarter: filters?.quarter || undefined,
                        datasetTag: (filters as any)?.datasetTag || undefined
                    }
                })
            });

            const result = await response.json();
            console.log('💬 Chat result:', result);

            if (result.success) {
                // Set conversation ID if this is the first message
                if (!conversationId && result.conversationId) {
                    setConversationId(result.conversationId);
                }

                // Add AI response to chat history
                const aiMessage = {
                    id: (Date.now() + 1).toString(),
                    type: 'assistant',
                    content: result.response || 'No response received',
                    timestamp: new Date()
                };
                setChatHistory(prev => [...prev, aiMessage]);
                setError(null);
            } else {
                setError(`Failed to get response: ${result.error}`);
            }
        } catch (err) {
            console.error('❌ Question error:', err);
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setLoading(false);
        }
    };

    const clearChat = () => {
        setChatHistory([]);
        setConversationId(null);
        setError(null);
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">AI Insights</h2>
                <p className="text-gray-600">AWS Bedrock Claude analysis of your voucher data</p>
            </div>

            {/* Quick Questions */}
            {chatHistory.length === 0 && (
                <div className="bg-white rounded-lg shadow p-4 mb-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Quick Questions</h4>
                    <div className="flex flex-wrap gap-2">
                        {[
                            "What's my current redemption rate?",
                            "Show me Q1 2025 performance",
                            "Which vouchers are expiring soon?",
                            "What's the pass rate by certification type?",
                            "How many vouchers were redeemed this quarter?"
                        ].map((quickQuestion, index) => (
                            <button
                                key={index}
                                onClick={() => setQuestion(quickQuestion)}
                                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                            >
                                {quickQuestion}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Chat Interface */}
            <div className="bg-white rounded-lg shadow flex flex-col" style={{ height: '60vh', minHeight: '400px' }}>
                {/* Chat Header */}
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-semibold">Chat with Claude</h3>
                        <p className="text-sm text-gray-600">Ask questions about your voucher data</p>
                    </div>
                    {chatHistory.length > 0 && (
                        <button
                            onClick={clearChat}
                            className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-50"
                        >
                            Clear Chat
                        </button>
                    )}
                </div>

                {/* Chat Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {chatHistory.length === 0 ? (
                        <div className="flex items-center justify-center h-full text-gray-500">
                            <div className="text-center">
                                <div className="text-4xl mb-3">🤖</div>
                                <p className="text-lg font-medium mb-2">Chat with Claude</p>
                                <p className="text-sm">Ask questions about your voucher data and get AI-powered insights</p>
                                <div className="mt-4 text-xs text-gray-400">
                                    <p>💡 Try: "What's my redemption rate?" or "Show me Q1 performance"</p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            {chatHistory.map((message, index) => (
                                <div key={message.id || index} className={`mb-4 flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`flex items-start space-x-2 max-w-[85%] ${message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                                        {/* Avatar */}
                                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${message.type === 'user'
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-200 text-gray-700'
                                            }`}>
                                            {message.type === 'user' ? 'You' : '🤖'}
                                        </div>

                                        {/* Message bubble */}
                                        <div className={`flex flex-col ${message.type === 'user' ? 'items-end' : 'items-start'}`}>
                                            <div className={`px-4 py-2 rounded-2xl text-left ${message.type === 'user'
                                                ? 'bg-blue-600 text-white rounded-br-md'
                                                : 'bg-gray-100 text-gray-900 rounded-bl-md border border-gray-200'
                                                }`}>
                                                <div className="text-sm whitespace-pre-wrap leading-relaxed text-left">
                                                    {message.content}
                                                </div>
                                            </div>
                                            <div className={`text-xs text-gray-500 mt-1 px-2 ${message.type === 'user' ? 'text-right' : 'text-left'}`}>
                                                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {/* Typing indicator */}
                            {loading && (
                                <div className="mb-4 flex justify-start">
                                    <div className="flex items-start space-x-2 max-w-[85%]">
                                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center text-sm font-medium">
                                            🤖
                                        </div>
                                        <div className="flex flex-col items-start">
                                            <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-gray-100 border border-gray-200">
                                                <div className="flex items-center space-x-2">
                                                    <div className="flex space-x-1">
                                                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                                                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                                    </div>
                                                    <span className="text-sm text-gray-600">Claude is analyzing your data...</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div ref={chatEndRef} />
                        </>
                    )}
                </div>

                {/* Chat Input */}
                <div className="p-4 border-t border-gray-200 bg-gray-50">
                    {error && (
                        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-center space-x-2">
                                <span className="text-red-500">⚠️</span>
                                <span className="text-sm text-red-700">{error}</span>
                                <button
                                    onClick={() => setError(null)}
                                    className="ml-auto text-red-500 hover:text-red-700"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="flex space-x-3">
                        <div className="flex-1 relative">
                            <input
                                type="text"
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && askQuestion()}
                                placeholder="Ask Claude about your voucher data..."
                                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white placeholder-gray-500 shadow-sm"
                                disabled={loading}
                            />
                            <button
                                onClick={askQuestion}
                                disabled={loading || !question.trim()}
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center transition-colors"
                            >
                                {loading ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                ) : (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                        <div className="flex items-center space-x-4">
                            {conversationId && (
                                <span>Session: {conversationId.substring(0, 8)}...</span>
                            )}
                            <span>{chatHistory.length} messages</span>
                        </div>
                        <div className="text-gray-400">
                            Press Enter to send
                        </div>
                    </div>
                </div>
            </div>

            {/* Controls */}
            <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Pre-built Analysis</h3>

                <div className="flex flex-wrap gap-3 mb-4">
                    <button
                        onClick={testBedrock}
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                    >
                        {loading ? 'Testing...' : 'Test Bedrock Connection'}
                    </button>

                    <button
                        onClick={() => generateInsights('utilization')}
                        disabled={loading}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
                    >
                        {loading ? 'Generating...' : 'Utilization Insights'}
                    </button>

                    <button
                        onClick={() => generateInsights('performance')}
                        disabled={loading}
                        className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-400"
                    >
                        {loading ? 'Generating...' : 'Performance Insights'}
                    </button>

                    <button
                        onClick={() => generateInsights('comprehensive')}
                        disabled={loading}
                        className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:bg-gray-400"
                    >
                        {loading ? 'Generating...' : 'Comprehensive Analysis'}
                    </button>
                </div>

                {loading && (
                    <div className="flex items-center space-x-2 text-blue-600">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span>Generating AI insights...</span>
                    </div>
                )}
            </div>

            {/* Insights Display */}
            {insights.length > 0 && (
                <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold mb-4">Generated Insights</h3>
                    <div className="space-y-4">
                        {insights.map((insight, index) => (
                            <div key={index} className="border-l-4 border-blue-500 pl-4 py-2">
                                <div className="flex items-center space-x-2 mb-2">
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${insight.type === 'recommendation' ? 'bg-green-100 text-green-800' :
                                        insight.type === 'trend' ? 'bg-blue-100 text-blue-800' :
                                            'bg-yellow-100 text-yellow-800'
                                        }`}>
                                        {insight.type.toUpperCase()}
                                    </span>
                                    <span className="text-sm text-gray-500">
                                        Confidence: {(insight.confidence * 100).toFixed(0)}%
                                    </span>
                                </div>
                                <h4 className="font-medium text-gray-900 mb-1">{insight.title}</h4>
                                <p className="text-gray-600 text-sm">{insight.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Debug Section */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-medium text-yellow-800 mb-2">Debug Info</h4>
                <div className="text-xs text-yellow-700 space-y-1">
                    <div>Chat History Length: {chatHistory.length}</div>
                    <div>Loading: {loading.toString()}</div>
                    <div>Error: {error || 'None'}</div>
                    <div>Insights Count: {insights.length}</div>
                </div>
            </div>

            {insights.length === 0 && !loading && !error && (
                <div className="bg-gray-50 rounded-lg p-6 text-center">
                    <p className="text-gray-500">Click a button above to generate AI insights about your voucher data</p>
                </div>
            )}
        </div>
    );
};